import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { ChargerModel, Vendor } from "../types";
import { useToast } from "../toast";

interface Props {
  open: boolean;
  model: ChargerModel | null;
  onClose: () => void;
  onSaved: () => void;
}

const empty = {
  vendor_id: "",
  model_name: "",
  max_power_kw: "",
  description: "",
};

export default function ChargerModelDialog({ open, model, onClose, onSaved }: Props) {
  const api = useAuthApi();
  const [form, setForm] = useState(empty);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    api<Vendor[]>(`${MGMT}/vendors`)
      .then((data) => setVendors(data ?? []))
      .catch((e) => toast.show((e as Error).message, "error"));
    setForm(
      model
        ? {
            vendor_id: String(model.vendor_id),
            model_name: model.model_name,
            max_power_kw: model.max_power_kw != null ? String(model.max_power_kw) : "",
            description: model.description ?? "",
          }
        : empty
    );
  }, [api, open, model, toast]);

  const save = async () => {
    if (!form.vendor_id) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        vendor_id: parseInt(form.vendor_id, 10),
        model_name: form.model_name,
        max_power_kw: form.max_power_kw !== "" ? parseFloat(form.max_power_kw) : null,
        description: form.description || null,
      };
      if (model) {
        await api(`${MGMT}/charger-models/${model.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        toast.show("Charger model updated");
      } else {
        await api(`${MGMT}/charger-models`, {
          method: "POST",
          body: JSON.stringify(body),
        });
        toast.show("Charger model created");
      }
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{model ? "Edit Charger Model" : "Add Charger Model"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth required>
            <InputLabel>Vendor</InputLabel>
            <Select
              label="Vendor"
              value={form.vendor_id}
              onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
            >
              {vendors.map((vendor) => (
                <MenuItem key={vendor.id} value={String(vendor.id)}>
                  {vendor.label?.value && vendor.label.value !== vendor.name
                    ? `${vendor.label.value} (${vendor.name})`
                    : vendor.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {vendors.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No vendors available yet. Create one in the Vendors section first.
            </Typography>
          )}
          <TextField
            label="Model Name"
            required
            placeholder="e.g. Terra 54, Pulsar Plus"
            value={form.model_name}
            onChange={(e) => setForm({ ...form, model_name: e.target.value })}
          />
          <TextField
            label="Max Power (kW)"
            type="number"
            inputProps={{ step: 0.1 }}
            value={form.max_power_kw}
            onChange={(e) => setForm({ ...form, max_power_kw: e.target.value })}
          />
          <TextField
            label="Description"
            placeholder="Optional notes…"
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={save}
          variant="contained"
          disabled={saving || !form.vendor_id || !form.model_name}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
